import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import React, { useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Header from '../../../components/page-layout/header';
import TitleSection from '../../../components/page-layout/title-section';
import ViewListSubHeader from '../../../components/page-layout/view-list-sub-header';
import ShipTogetherName from '../../../components/order-alerts/ship-together/ship-together-name';
import ActionsSection from '../../../components/page-layout/actions-section';
import ShipTogetherButton from '../../../components/alerts/ship-together-button';
import { OrdersGridPrintDocumentButton } from '../../../components/orders-grid/print-document-button';
import ToggleSidebarButton from '../../../pages/orders-page/toggle-sidebar-button';
import EditColumnsButtonContainer from '../../../components/edit-columns-button/containers/orders-grid-container';
import { OrdersGridContainerShipmentMode } from '../../../pages/orders-page/containers/orders-grid-container/orders-grid-container.shipment-mode';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { OrdersGridDrawerContainer } from '../../../containers/orders-drawer';
import ErrorBoundary from '../../../components/error-boundary';
import Global from '../../../store/global-selectors';
import { getAlertDetails } from '../../../store/alerts/details/alerts.details.actions';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { usePrevious } from '../../../hooks/use-previous';
import { PagingControl } from '../../../components/paging-control';
import { calculatePagingStats } from '../../../services/pagination-service';
import { setViewPageAndPageSize } from '../../../store/paging/paging.actions';
import { orderAlertsGridChangePage, setSelectedAlertIds } from '../../../store/alerts/grid/alerts.grid.actions';

import style from './alerts-detail-page.module.less';

const pagingViewName = 'alerts.details';

const AlertsDetailPage = () => {
  const dispatch = useDispatch();
  const alertDetails = useSelector(Global.selectAlertsDetailsAlertDetails);
  const isSidebarVisible = useSelector(Global.selectOrdersGridisSidebarVisible);
  const isLoading = useSelector(Global.selectOrdersGridIsLoading);
  const pagingStats = useSelector(
    (state) => Global.selectPagingViewPagingStats(state, { viewName: pagingViewName }) || calculatePagingStats(1, 100, 0),
  );
  const { tabFilter, alertId } = useParams();
  const previousAlertId = usePrevious(alertId);
  const backPath = `/orders/alerts/${tabFilter || 'shiptogether'}`;

  useOnComponentMount(() => {
    if (!alertId) {
      return;
    }
    dispatch(getAlertDetails(alertId));
  });

  useEffect(() => {
    if (alertId === previousAlertId) {
      return;
    }
    dispatch(getAlertDetails(alertId));
  }, [alertId, dispatch, previousAlertId]);

  const onPageChange = useCallback(
    (page, pageSize) => {
      dispatch(setViewPageAndPageSize(pagingViewName, page, pageSize));
      dispatch(orderAlertsGridChangePage(page, pageSize));
      dispatch(getAlertDetails());
    },
    [dispatch],
  );

  const handleGoBack = useCallback(() => {
    dispatch(setSelectedAlertIds([alertId]));
  }, [alertId, dispatch]);

  if (!alertDetails) {
    return <LoadingIndicator className={style['loading-indicator']} />;
  }

  const { fulfillmentPlanIds, address } = alertDetails;
  const count = fulfillmentPlanIds.length;

  return (
    <div className={style['alert-detail-page']}>
      <Header>
        <TitleSection>
          <Link className={style['go-back-to-batches-button']} onClick={handleGoBack} theme='unstyled' to={backPath}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          {!isLoading && <ShipTogetherName count={count} name={address.name} />}
        </TitleSection>
        <EditColumnsButtonContainer />
      </Header>
      <ActionsSection className={style['actions-section']}>
        <ShipTogetherButton />
        <OrdersGridPrintDocumentButton />
      </ActionsSection>
      <ViewListSubHeader>
        <div className={style['edit-grid-wrap']}>
          <div className={style['alert-supplemental']}>
            <FormattedMessage
              defaultMessage='Consider shipping these orders together to save on shipping costs.'
              description='alertsSupplemental'
              id='eaFIOx'
            />
          </div>
          <div className={style['edit-columns-and-drawer']}>
            <ToggleSidebarButton />
          </div>
        </div>
      </ViewListSubHeader>
      {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
      {!isLoading && (
        <div className={style['grid-content']}>
          <div
            className={classnames(style['grid-and-footer'], {
              [style['no-sidebar']]: !isSidebarVisible,
            })}
          >
            <div className={style.grid}>
              <ErrorBoundary errorMessageId='ordersGrid'>
                <OrdersGridContainerShipmentMode />
              </ErrorBoundary>
            </div>
            <div className={[style['paging-footer']]}>
              <PagingControl onPageChange={onPageChange} {...pagingStats} />
            </div>
          </div>
          {isSidebarVisible && (
            <div className={style['side-bar']}>
              <ErrorBoundary errorMessageId='drawer'>
                <OrdersGridDrawerContainer />
              </ErrorBoundary>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertsDetailPage;
